import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { SPECIMEN_TYPE_URL_MAP } from 'src/constants/specimen_types';
import { SpecimenTypeInfo } from 'src/hooks/use_ordering_specimen_types';
import { SpecimenType } from 'src/types';
import { SpecimenTypeUtils } from 'src/utils';

interface SpecimenTypeSelectorProps {
  isOpen: boolean;
  specimenTypes: SpecimenTypeInfo[];
  onSelect: (specimenType: SpecimenType) => void;
  onCancel: () => void;
}

const OptionContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(0.5, 0),
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:first-of-type': {
    marginTop: 0,
  },
  '&:last-of-type': {
    marginBottom: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  margin: 0,
  alignItems: 'center',
  '& .MuiRadio-root': {
    paddingRight: theme.spacing(1),
  },
  '& .MuiFormControlLabel-label': {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.subtitle1.fontSize,
    color: theme.palette.text.primary,
  },
}));

const OptionDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.subtitle2.fontSize,
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(1),
}));

export const SpecimenTypeSelector: React.FC<SpecimenTypeSelectorProps> = ({
  isOpen,
  specimenTypes,
  onSelect,
  onCancel,
}) => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter to only show enabled types
  const enabledTypes = specimenTypes.filter((type) => type.enabled);

  // Get selected value from URL params
  const selectedUrlValue = searchParams.get('specimen_type') || '';
  const specimenTypeFromUrl =
    SpecimenTypeUtils.URL_TO_SPECIMEN_TYPE_MAP[selectedUrlValue] || null;

  // Only allow selection if the specimen type is both valid AND enabled
  const selectedSpecimenType =
    specimenTypeFromUrl &&
    enabledTypes.some((type) => type.type === specimenTypeFromUrl)
      ? specimenTypeFromUrl
      : null;

  // Clear invalid specimen type from URL if it's not enabled
  useEffect(() => {
    if (specimenTypeFromUrl && !selectedSpecimenType) {
      // Remove the invalid specimen_type from URL
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('specimen_type');
        return newParams;
      });
    }
  }, [specimenTypeFromUrl, selectedSpecimenType, setSearchParams]);

  if (enabledTypes.length === 0) {
    return (
      <Dialog open={isOpen} onClose={onCancel} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ textAlign: 'left' }}>{'Order Type'}</DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            No test types are available for your current region.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button onClick={onCancel}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const handleContinue = () => {
    if (selectedSpecimenType) {
      onSelect(selectedSpecimenType);
    }
  };

  const handleOptionClick = (specimenType: SpecimenType) => {
    const urlValue = SPECIMEN_TYPE_URL_MAP[specimenType];
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('specimen_type', urlValue);
      return newParams;
    });
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ textAlign: 'left' }}>{'Order Type'}</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {enabledTypes.map((specimenType) => (
          <OptionContainer
            key={specimenType.type}
            onClick={() => handleOptionClick(specimenType.type)}
          >
            <StyledFormControlLabel
              value={specimenType.type}
              control={
                <Radio
                  checked={selectedSpecimenType === specimenType.type}
                  value={specimenType.type}
                  disableRipple
                  sx={{ marginLeft: theme.spacing(-0.25), py: 0 }}
                />
              }
              label={specimenType.displayName}
            />
            <OptionDescription>{specimenType.description}</OptionDescription>
          </OptionContainer>
        ))}
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'end' }}>
        <Button size='large' onClick={onCancel}>
          CLOSE
        </Button>
        <Box
          sx={{
            width: theme.spacing(2),
          }}
        />
        <Button
          onClick={handleContinue}
          variant='contained'
          size='large'
          disabled={!selectedSpecimenType}
        >
          CONTINUE
        </Button>
      </DialogActions>
    </Dialog>
  );
};
